'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ArrowRight, ArrowUpRight, Menu, X } from 'lucide-react';
import config from '@/config';
import Logo from './Logo';
import { cn } from '@/src/libs/utils';

type NavLink = {
  href: string;
  label: string;
};

type LinkAction = {
  href: string;
  label: string;
  /** target="_blank" etc. Optional. */
  target?: string;
  /** Forwarded to the anchor. Use to attach mixpanel / analytics calls. */
  onClick?: () => void;
};

type SiteHeaderProps = {
  /** Where the logo points. Defaults to '/'. */
  homeHref?: string;
  /** Optional italic label after the brand name (e.g. "for Business"). */
  subLabel?: string;
  /** Primary nav links rendered next to the logo. */
  navLinks: NavLink[];
  /**
   * Optional outlined utility pill on the right (e.g. "Join a game").
   * Visually distinct from the auth links — use for cross-audience utility.
   */
  utility?: LinkAction;
  /** Secondary text-link action (e.g. "Log in" or "Try it free"). */
  secondaryAction: LinkAction;
  /** Primary filled-button CTA. */
  primaryAction: LinkAction;
};

export default function SiteHeader({
  homeHref = '/',
  subLabel,
  navLinks,
  utility,
  secondaryAction,
  primaryAction,
}: SiteHeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  const close = () => setIsOpen(false);

  return (
    <>
      <header className="sticky top-0 z-50 bg-paper-50/85 backdrop-blur-lg border-b border-ink-900/10">
        <nav
          className="max-w-[1440px] flex items-center justify-between px-6 lg:px-8 py-4 mx-auto"
          aria-label="Global"
        >
          {/* Left: logo + primary nav */}
          <div className="flex items-center gap-8 lg:gap-10">
            <Link
              className="flex items-center gap-2.5 shrink-0"
              href={homeHref}
              title={`${config.appName} homepage`}
            >
              <Logo />
              <span className="font-display font-black text-xl tracking-tight text-ink-900">
                {config.appName}
              </span>
              {subLabel && (
                <span className="hidden sm:inline-block font-display italic text-ink-500 text-base">
                  {subLabel}
                </span>
              )}
            </Link>

            <div className="hidden lg:flex lg:items-center lg:gap-7">
              {navLinks.map((link) => (
                <Link
                  href={link.href}
                  key={link.href}
                  className={cn(
                    'font-display text-base text-ink-600 hover:text-brand-600 transition-colors',
                    pathname === link.href && 'text-brand-600'
                  )}
                  title={link.label}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile menu trigger */}
          <div className="flex lg:hidden">
            <button
              type="button"
              className="p-2 rounded-lg hover:bg-paper-200 transition-colors"
              onClick={() => setIsOpen(true)}
              aria-label="Open main menu"
            >
              <Menu className="w-5 h-5 text-ink-900" />
            </button>
          </div>

          {/* Right: utility + auth + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {utility && (
              <>
                <a
                  href={utility.href}
                  onClick={utility.onClick}
                  target={utility.target}
                  className="group inline-flex items-center gap-1.5 rounded-full border border-ink-900/15 bg-paper-50 px-3.5 py-1.5 font-display text-sm text-ink-700 hover:border-ink-900/40 hover:text-ink-900 hover:bg-paper-100 transition-colors"
                >
                  {utility.label}
                  <ArrowUpRight className="w-3.5 h-3.5 -mr-0.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <span
                  className="h-5 w-px bg-ink-900/15"
                  aria-hidden="true"
                />
              </>
            )}
            <a
              href={secondaryAction.href}
              onClick={secondaryAction.onClick}
              target={secondaryAction.target}
              className="font-display text-base text-ink-600 hover:text-brand-600 transition-colors"
            >
              {secondaryAction.label}
            </a>
            <a
              className="btn btn-primary rounded-full px-5 text-white shrink-button"
              href={primaryAction.href}
              onClick={primaryAction.onClick}
              target={primaryAction.target}
              rel={
                primaryAction.target === '_blank'
                  ? 'noopener noreferrer'
                  : undefined
              }
            >
              {primaryAction.label}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </nav>
      </header>

      {/* Mobile menu */}
      <div className={`relative z-[60] ${isOpen ? '' : 'hidden'}`}>
        <div
          className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm"
          onClick={close}
        />
        <div className="fixed inset-y-0 right-0 z-10 w-full max-w-sm bg-paper-50 shadow-2xl px-6 py-4">
          <div className="flex items-center justify-between">
            <Link
              className="flex items-center gap-2.5 shrink-0"
              title={`${config.appName} homepage`}
              href={homeHref}
            >
              <Logo />
              <span className="font-display font-black text-xl text-ink-900">
                {config.appName}
              </span>
              {subLabel && (
                <span className="font-display italic text-ink-500 text-base">
                  {subLabel}
                </span>
              )}
            </Link>
            <button
              type="button"
              className="p-2 rounded-lg hover:bg-paper-200 transition-colors"
              onClick={close}
              aria-label="Close menu"
            >
              <X className="w-5 h-5 text-ink-900" />
            </button>
          </div>

          <div className="mt-8 flow-root">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  href={link.href}
                  key={link.href}
                  className={cn(
                    'px-3 py-2.5 rounded-lg font-display text-base text-ink-600 hover:bg-paper-200 transition-colors',
                    pathname === link.href && 'text-brand-600 bg-paper-200'
                  )}
                  title={link.label}
                  onClick={close}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="my-6 h-px bg-ink-900/15" />

            <div className="flex flex-col gap-3">
              {utility && (
                <a
                  href={utility.href}
                  onClick={() => {
                    utility.onClick?.();
                    close();
                  }}
                  target={utility.target}
                  className="btn btn-ghost btn-sm rounded-full text-ink-900"
                >
                  {utility.label}
                </a>
              )}
              <a
                href={secondaryAction.href}
                onClick={secondaryAction.onClick}
                target={secondaryAction.target}
                className="btn btn-ghost btn-sm rounded-full text-ink-900"
              >
                {secondaryAction.label}
              </a>
              <a
                className="btn btn-primary btn-sm rounded-full text-white"
                href={primaryAction.href}
                onClick={primaryAction.onClick}
                target={primaryAction.target}
                rel={
                  primaryAction.target === '_blank'
                    ? 'noopener noreferrer'
                    : undefined
                }
              >
                {primaryAction.label}
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
